import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  DeleteIconComponent,
  AddIconComponent,
  CloseIconComponent,
} from '@kuiper/icons';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { assigneeSelectDependencyEmpty } from '../../../workflow.constants';
import { EscalationTypeEnum, SlaTypeEnum } from '../sla-type.enum';
import { AssigneeInputDependency, WorkFlowStep } from '../../../workflow.model';
import { AccordionComponent } from '../../../../../../../ui-components/lib/accordion/accordion.component';
import { CustomizedDropdownComponent } from '../../../../../../../ui-components/lib/customized-dropdown/customized-dropdown.component';
import { PaginatedSelectComponent } from '../../../../../../../ui-components/lib/paginated-select/paginated-select.component';
import { SelectComponent } from '../../../../../../../ui-components/lib/select/select.component';
import { ToggleComponent } from '../../../../../../../ui-components/lib/toggle/toggle.component';
import { ValidationMessageComponent } from '../../../../../../../ui-components/lib/validation-message/validation-message.component';
import { InputComponent } from '../../../../../../../ui-components/lib/input/input.component';
import { LookupOptionItem } from '../../../../../../../ui-components/lib/lookup-field/lookup-field-item';
import { OptionItem } from '../../../../../../../ui-components/lib/option.interface';
import { FEATURE_FLAGS } from '../../../../../../common/constants/feature-flags.constants';
import { baseUrl } from '../../../../../../common/constants/endpoints.constants';
import { FeatureFlagsService } from '../../../../../../common/services/feature-flags/feature-flags.service';
import {
  createStepActionFormGroup,
  DUPLICATED_ACTION_ERROR,
  mapStepActionUsersToOptions,
  StepActionFormGroup,
} from './workflow-configuration-step-action/workflow-configuration-step-action.const';
import { WorkflowConfigurationStepActionComponent } from './workflow-configuration-step-action/workflow-configuration-step-action.component';
import { ReflectionScopeEnum } from '../../../reflections/enums/reflection-scope.enum';
import { ReflectionListComponent } from '../../../reflections/reflection-list/reflection-list.component';

@Component({
  selector: 'lib-workflow-step-info-form',
  imports: [
    TranslocoPipe,
    AccordionComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    InputComponent,
    SelectComponent,
    CustomizedDropdownComponent,
    ValidationMessageComponent,
    PaginatedSelectComponent,
    ToggleComponent,
    DeleteIconComponent,
    AddIconComponent,
    CloseIconComponent,
    WorkflowConfigurationStepActionComponent,
    ReflectionListComponent,
  ],
  templateUrl: './workflow-step-info-form.component.html',
  styleUrl: './workflow-step-info-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkflowStepInfoFormComponent implements OnInit {
  transloco = inject(TranslocoService);
  activatedRoute = inject(ActivatedRoute);
  private readonly featureFlagsService = inject(FeatureFlagsService);

  readonly isAddCollaboratorsStepActionEnabled = signal(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOW_CONFIGURATION.WORKFLOW_STEP_ACTIONS
        .add_collaborators
    )
  );

  stepInfo = input.required<WorkFlowStep>();
  stepIndex = input.required<number>();
  stepInfoForm = input.required<FormGroup>();
  isFormChanged = input<boolean>(false);
  isUpdateStepLoading = input<boolean>(false);
  assigneeSelectDependency = input<AssigneeInputDependency[]>([]);
  resourceSelectDependency = input<AssigneeInputDependency>(
    assigneeSelectDependencyEmpty
  );
  assigneeReferenceArray = input<any>();
  currentStep = input<WorkFlowStep | null>();
  addFromGroupEmitter = output<FormGroup>();
  deleteFromGroupIndexEmitter = output<number>();
  disableExpand = input<boolean>(false);
  canBeSentBackToFirstPosition = input<boolean | undefined>(undefined);
  hasStepActions = input<boolean>(true);
  // Embedded hosts (custom workflow canvas) have no workflowId route param.
  flowTemplateId = input<string | null>(null);
  /** The flow template whose step reflections are listed (editable); `null` hides the section. */
  reflectionsFlowTemplateId = input<number | null>(null);
  discardChanges = output();
  submitForm = output<WorkFlowStep>();

  readonly stepActionInitialUsersById = computed(() => {
    const usersById: Record<number, OptionItem[]> = {};

    for (const action of this.stepInfo().step_actions ?? []) {
      usersById[action.id] = mapStepActionUsersToOptions(action.users);
    }

    return usersById;
  });

  workflowId = '';
  slaVariableApi = '';
  escalationVariableApi = '';
  slaTypeEnum = SlaTypeEnum;
  protected readonly Scope = ReflectionScopeEnum;
  selectedSlaVariable = signal<LookupOptionItem | undefined>(undefined);
  selectedEscalationVariable = signal<LookupOptionItem | undefined>(undefined);
  selectedSlaType: SlaTypeEnum.STATIC | SlaTypeEnum.VARIABLE | null = null;
  escalationTypeEnum = EscalationTypeEnum;
  readonly Validators = Validators;
  readonly duplicatedActionError = DUPLICATED_ACTION_ERROR;
  assigneeTypeOptions = computed<OptionItem[]>(() => {
    const options: OptionItem[] = [
      {
        label: this.transloco.translate(
          'workflow_step_info_form_{department}_label'
        ),
        value: 'department',
      },
      {
        label: this.transloco.translate(
          'workflow_step_info_form_{group}_label'
        ),
        value: 'group',
      },
      {
        label: this.transloco.translate(
          'workflow_step_info_form_{legal_entity}_label'
        ),
        value: 'legal_entity',
      },
      {
        label: this.transloco.translate('workflow_step_info_form_{user}_label'),
        value: 'user',
      },
      {
        label: this.transloco.translate(
          'workflow_step_info_form_{variables}_label'
        ),
        value: 'variable',
      },
      {
        label: this.transloco.translate(
          'workflow_step_info_form_{project_role}_label'
        ),
        value: 'project_role',
      },
      {
        label: this.transloco.translate(
          'workflow_step_info_form_{fields}_label'
        ),
        value: 'fields',
      },
      {
        label: this.transloco.translate(
          'workflow_step_info_form_{position}_label'
        ),
        value: 'position',
      },
    ];

    return options;
  });

  groupAssignableOption = signal<OptionItem[]>([
    {
      label: this.transloco.translate('workflow_step_info_form_member_label'),
      value: 'members',
    },
  ]);

  departmentAssignableOption = signal<OptionItem[]>([
    {
      label: this.transloco.translate('workflow_step_info_form_manger_label'),
      value: 'manager',
    },
    {
      label: this.transloco.translate('workflow_step_info_form_member_label'),
      value: 'members',
    },
    {
      label: this.transloco.translate(
        'workflow_step_info_form_member_sub_department_members_label'
      ),
      value: 'members_and_sub_members',
    },
  ]);

  stakeholderTypeOptions = signal<OptionItem[]>([
    {
      label: this.transloco.translate(
        'workflow_step_info_form_{department}_label'
      ),
      value: 'department',
    },
    {
      label: this.transloco.translate('workflow_step_info_form_{group}_label'),
      value: 'group',
    },
    {
      label: this.transloco.translate('workflow_step_info_form_{user}_label'),
      value: 'user',
    },
    {
      label: this.transloco.translate(
        'workflow_step_info_form_{variables}_label'
      ),
      value: EscalationTypeEnum.VARIABLE,
    },
  ]);

  escalationTypeOptions = signal<OptionItem[]>([
    {
      label: this.transloco.translate(
        'workflow_step_info_form_send_email_label'
      ),
      value: 'send_email',
    },
  ]);

  assignmentPreferenceOptions = signal<OptionItem[]>([
    {
      label: this.transloco.translate(
        'workflow_step_info_form_all_assignees_label'
      ),
      value: 'all_assignees',
    },
    {
      label: this.transloco.translate('workflow_step_info_form_at_least_label'),
      value: 'at_least',
    },
  ]);

  assignmentPreferenceTypeOptions = signal<OptionItem[]>([
    {
      label: this.transloco.translate('workflow_step_info_form_and_label'),
      value: 'and',
    },
  ]);

  slaTypeOptions = signal<OptionItem[]>([
    {
      label: this.transloco.translate('workflow_step_info_form_static_label'),
      value: SlaTypeEnum.STATIC,
    },
    {
      label: this.transloco.translate('workflow_step_info_form_variable_label'),
      value: SlaTypeEnum.VARIABLE,
    },
  ]);

  whenTypeOptions = signal<OptionItem[]>([
    {
      label: this.transloco.translate(
        'workflow_step_info_form_at_deadline_label'
      ),
      value: 'at_deadline',
    },
  ]);

  completionActionOptions = signal<OptionItem[]>([
    {
      label: this.transloco.translate(
        'workflow_configurations_details_screen_completion_action_complete'
      ),
      value: 'complete',
    },
    {
      label: this.transloco.translate(
        'workflow_configurations_details_screen_completion_action_approve'
      ),
      value: 'approve',
    },
    {
      label: this.transloco.translate(
        'workflow_configurations_details_screen_completion_action_submit'
      ),
      value: 'submit',
    },
  ]);

  private readonly assigneeAssignableTypes = signal<string[]>([]);

  readonly assigneeLookupLabels = computed(() =>
    this.assigneeAssignableTypes().map(type =>
      this.lookupLabelForAssignableType(type)
    )
  );

  private readonly trackAssigneeAssignableTypes = effect(() => {
    this.assigneeReferenceArray();
    this.stepInfoForm();
    this.syncAssigneeAssignableTypes();
  });

  sendBackStepsOptions = computed<OptionItem[]>(() => {
    const options = [];
    const maxSteps = this.canBeSentBackToFirstPosition()
      ? this.stepInfo().position - 1
      : this.stepInfo().position - 2;
    for (let i = 0; i < maxSteps; i++) {
      options.push({
        label:
          i +
          1 +
          ' ' +
          this.transloco.translate(
            'workflow_configurations_details_screen_step_back_option_label'
          ),
        value: (i + 1).toString(),
      });
    }
    return options;
  });

  get assigneeForm(): FormArray {
    return this.stepInfoForm().get('assignees') as FormArray;
  }

  get assigneeControls(): FormGroup[] {
    return this.assigneeForm.controls as FormGroup[];
  }

  /**************************** Actions Form ****************************/
  get actionsForm(): FormGroup {
    return this.stepInfoForm().get('actions') as FormGroup;
  }

  get stepActionsForm(): FormArray | null {
    return this.stepInfoForm().get('step_actions') as FormArray | null;
  }

  get stepActionControls(): StepActionFormGroup[] {
    return (this.stepActionsForm?.controls ?? []) as StepActionFormGroup[];
  }

  assignmentPreferenceControl(index: number): string | null {
    return this.assigneeForm.controls[index].get('assignment_preference')
      ?.value;
  }

  get escalationForm(): FormGroup {
    return this.stepInfoForm().get('escalation') as FormGroup;
  }

  get escalationTypeControl(): FormControl {
    return this.escalationForm.get('type') as FormControl;
  }

  get stakeholderTypeControl(): FormControl {
    return this.escalationForm.get('stakeholder_type') as FormControl;
  }

  ngOnInit() {
    this.syncAssigneeAssignableTypes();
    this.workflowId = this.flowTemplateId() || this.activatedRoute.snapshot.params['workflowId'];
    this.slaVariableApi = `${baseUrl}/lookup/flow-templates/${this.workflowId}/sla-variables`;
    this.escalationVariableApi = `${baseUrl}/lookup/flow-templates/${this.workflowId}/sla-escalation-variables`;
    this.selectedSlaType = this.stepInfo().sla.type as
      | SlaTypeEnum.STATIC
      | SlaTypeEnum.VARIABLE
      | null;
    if (this.selectedSlaType === SlaTypeEnum.VARIABLE) {
      this.selectedSlaVariable.set({
        label: this.stepInfo().sla.variable.label,
        value: this.stepInfo().sla.variable.value,
      } as LookupOptionItem);
    }

    if (this.escalationTypeControl.value === EscalationTypeEnum.VARIABLE) {
      this.selectedEscalationVariable.set({
        label: this.stepInfo().escalation?.stakeholder_data?.name,
        value: this.stepInfo().escalation?.stakeholder_data?.id,
      } as LookupOptionItem);
    }

    // disable send back and send back steps if position is 2
    // because send back is not allowed in the second step
    if (
      this.stepInfo().position === 2 &&
      !this.canBeSentBackToFirstPosition()
    ) {
      this.actionsForm.get('send_back')?.setValue(false);
      this.actionsForm.get('send_back')?.disable();
      this.actionsForm.get('send_back_steps')?.setValue(null);
      this.actionsForm.get('send_back_steps')?.disable();
    }
  }

  toggleAccordion(step: WorkFlowStep) {
    if (this.disableExpand()) {
      this.discardChanges.emit();
    } else {
      step.isExpanded = !step.isExpanded;
    }
  }

  addAssignee() {
    const assigneeFormGroup = new FormGroup({
      assignable_type: new FormControl('', Validators.required),
      assignable_id: new FormControl('', Validators.required),
      preference: new FormControl(null, Validators.required),
      option: new FormControl(null, Validators.required),
      assignment_preference: new FormControl(null, Validators.required),
    });
    this.addFromGroupEmitter.emit(assigneeFormGroup);
  }

  changeAssignmentPreference(selectedOption: OptionItem, index: number) {
    const formGroup = this.assigneeForm.controls[index] as FormGroup;
    const preferenceController = formGroup.controls['preference'];
    if (selectedOption.value == 'all_assignees') {
      preferenceController?.setValue(null);
      preferenceController?.removeValidators(Validators.required);
      preferenceController?.setErrors(null);
    } else {
      preferenceController?.addValidators(Validators.required);
    }
  }

  changeAssignableType(index: number) {
    const type = this.assigneeControls[index].get('assignable_type')?.value;

    if (
      type === 'user' ||
      type === 'legal_entity' ||
      type === 'variable' ||
      type === 'project_role' ||
      type === 'fields' ||
      type === 'position'
    ) {
      this.removePreferenceAndAssignmentPreferenceValidators(type, index);
    }

    const assignableIdControl =
      this.assigneeForm.controls[index].get('assignable_id');
    if (assignableIdControl?.value === '') {
      assignableIdControl.addValidators([Validators.required]);
      assignableIdControl.updateValueAndValidity();
    }

    const assignableOptionController =
      this.assigneeControls[index].get('option');
    const currentValue = assignableOptionController?.value;

    let availableOptions: any[] = [];
    if (type === 'group') {
      availableOptions = this.groupAssignableOption().map(opt => opt.value);
    } else if (type === 'department') {
      availableOptions = this.departmentAssignableOption().map(
        opt => opt.value
      );
    }

    if (
      type !== 'legal_entity' &&
      type !== 'user' &&
      type !== 'variable' &&
      type !== 'project_role' &&
      type !== 'fields' &&
      type !== 'position' &&
      (!currentValue || !availableOptions.includes(currentValue))
    ) {
      assignableOptionController?.reset();
    }

    if (
      type === 'legal_entity' ||
      type === 'user' ||
      type === 'variable' ||
      type === 'project_role' ||
      type === 'fields' ||
      type === 'position'
    ) {
      assignableOptionController?.setValue(null);
      assignableOptionController?.removeValidators(Validators.required);
      assignableOptionController?.setErrors(null);
      this.removePreferenceAndAssignmentPreferenceValidators(type, index);
    } else {
      assignableOptionController?.addValidators(Validators.required);
      assignableOptionController?.updateValueAndValidity();
    }

    if (this.currentStep()?.assignees[index]?.assignable_type !== type) {
      this.getFormControl(index, 'assignment_preference')?.reset();
      this.getFormControl(index, 'preference')?.reset();
      this.getFormControl(index, 'option')?.reset();
    }

    this.syncAssigneeAssignableTypes();
  }

  getFormControl(index: number, formControlName: string) {
    return this.assigneeControls[index].get(formControlName);
  }

  private syncAssigneeAssignableTypes(): void {
    const assignees = this.stepInfoForm()?.get('assignees') as FormArray | null;

    this.assigneeAssignableTypes.set(
      (assignees?.controls ?? []).map(
        control => (control.get('assignable_type')?.value as string) ?? ''
      )
    );
  }

  private lookupLabelForAssignableType(type: string): string {
    if (type === 'legal_entity') {
      return this.transloco.translate(
        'workflow_step_info_form_{legal_entity}_label'
      );
    }

    if (type === 'project_role') {
      return this.transloco.translate(
        'workflow_step_info_form_select_project_role_label'
      );
    }

    if (type === 'fields') {
      return this.transloco.translate(
        'workflow_step_info_form_select_{fields}_label'
      );
    }

    return this.transloco.translate(
      'workflow_step_info_form_variable option_label'
    );
  }

  changeAssignableOption(selectedOption: OptionItem, index: number) {
    const preferenceController = this.assigneeControls[index].get('preference');
    const assignmentPreferenceController = this.assigneeControls[index].get(
      'assignment_preference'
    );

    if (selectedOption.value === 'manager') {
      this.removePreferenceAndAssignmentPreferenceValidators('manager', index);
    } else {
      preferenceController?.addValidators(Validators.required);
      assignmentPreferenceController?.addValidators(Validators.required);
    }
  }

  removePreferenceAndAssignmentPreferenceValidators(
    type: string,
    index: number
  ) {
    const preferenceController = this.assigneeControls[index].get('preference');
    const assignmentPreferenceController = this.assigneeControls[index].get(
      'assignment_preference'
    );
    if (
      type === 'user' ||
      type == 'variable' ||
      type === 'manager' ||
      type === 'project_role' ||
      type === 'fields' ||
      type === 'position'
    ) {
      preferenceController?.setValue(null);
      preferenceController?.clearValidators();
      preferenceController?.setErrors(null);
      preferenceController?.updateValueAndValidity({
        onlySelf: false,
        emitEvent: true,
      });

      assignmentPreferenceController?.setValue(null);
      assignmentPreferenceController?.clearValidators();
      assignmentPreferenceController?.setErrors(null);
      assignmentPreferenceController?.updateValueAndValidity({
        onlySelf: false,
        emitEvent: true,
      });
    } else if (assignmentPreferenceController?.value === null) {
      assignmentPreferenceController.addValidators(Validators.required);
      assignmentPreferenceController.updateValueAndValidity({
        onlySelf: false,
        emitEvent: true,
      });
    }
  }

  changeSlaType(selectedOption: OptionItem) {
    const value = selectedOption.value as
      | SlaTypeEnum.STATIC
      | SlaTypeEnum.VARIABLE
      | null;
    const slaControl = this.stepInfoForm().get('sla') as FormControl;
    const slaVariableControl = this.stepInfoForm().get(
      'sla_variable'
    ) as FormControl;

    if (value !== this.selectedSlaType) {
      this.selectedSlaType = value;
    }

    if (value === SlaTypeEnum.STATIC) {
      slaControl.addValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(168),
      ]);
      slaVariableControl.clearValidators();
      slaVariableControl.reset();
    } else {
      slaVariableControl.addValidators(Validators.required);
      slaControl.clearValidators();
      slaControl.reset();
    }

    slaVariableControl.updateValueAndValidity();
    slaControl.updateValueAndValidity();
  }

  toggleSendBack(value: boolean) {
    const valueControl = value;
    const sendBackStepsControl = this.actionsForm.get('send_back_steps');
    if (valueControl) {
      sendBackStepsControl?.enable();
      sendBackStepsControl?.addValidators(Validators.required);
    } else {
      sendBackStepsControl?.disable();
      sendBackStepsControl?.clearValidators();
      sendBackStepsControl?.reset();
    }
    sendBackStepsControl?.updateValueAndValidity();
  }

  addStepAction() {
    this.stepActionsForm?.push(createStepActionFormGroup());
  }

  removeStepAction(index: number) {
    this.stepActionsForm?.removeAt(index);
  }
}
